import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

export default function ContactForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    // Frontend-only: brief delay then redirect to thank_you (Spanish page)
    setTimeout(() => {
      navigate("/thank_you");
    }, 550);
  };

  return (
    <form onSubmit={onSubmit} className="grid gap-6" data-testid="contact-form" style={{ wordBreak: "break-word" }}>
      <FloatingField
        label={t("contact.nameLabel")}
        placeholder={t("contact.namePlaceholder")}
        value={values.name}
        onChange={onChange("name")}
        required
        name="name"
        autoComplete="name"
        testId="contact-form-name-input"
      />
      <FloatingField
        label={t("contact.emailLabel")}
        placeholder={t("contact.emailPlaceholder")}
        value={values.email}
        onChange={onChange("email")}
        required
        type="email"
        name="email"
        autoComplete="email"
        testId="contact-form-email-input"
      />
      <FloatingField
        label={t("contact.messageLabel")}
        placeholder={t("contact.messagePlaceholder")}
        value={values.message}
        onChange={onChange("message")}
        required
        multiline
        name="message"
        testId="contact-form-message-input"
      />

      <div className="flex items-center justify-between gap-4 pt-2">
        <p className="text-xs text-[#7A7B82] max-w-xs leading-relaxed">
          {t("contact.consent")}
        </p>
        <motion.button
          whileHover={{ y: -2 }}
          whileTap={{ y: 0 }}
          type="submit"
          disabled={sending}
          data-testid="contact-form-submit-button"
          className="inline-flex items-center gap-2 bg-[#121316] hover:bg-[#C27835] text-[#F8F7F4] rounded-full px-6 py-3.5 text-sm font-medium transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {sending ? t("contact.sending") : t("contact.submit")}
          <ArrowUpRight size={16} />
        </motion.button>
      </div>
    </form>
  );
}

function FloatingField({ label, placeholder, value, onChange, required, type = "text", multiline, name, autoComplete, testId }) {
  const commonProps = {
    value,
    onChange,
    required,
    name,
    autoComplete,
    placeholder,
    "data-testid": testId,
    className:
      "w-full bg-transparent border-b border-[#121316]/25 focus:border-[#C27835] outline-none py-3 text-lg text-[#121316] placeholder:text-[#7A7B82] transition-colors duration-200",
  };
  return (
    <label className="block">
      <span className="block text-[11px] font-mono uppercase tracking-[0.22em] text-[#7A7B82]">{label}</span>
      {multiline ? (
        <textarea rows={3} {...commonProps} />
      ) : (
        <input type={type} {...commonProps} />
      )}
    </label>
  );
}
